/**
 * @file remoteDragLiveness.ts
 * @description Awareness is fire-and-forget: a collaborator's `draggingNodes`
 * entry stays in their state until they change it, so a tab that lost its
 * pointerup (alt-tab mid-drag, a suspended background tab) keeps advertising
 * a drag forever, and every other client keeps re-applying that stale
 * position. Every entry therefore carries a sender timestamp that the sender
 * refreshes on a heartbeat while the gesture is held, and receivers stamp
 * the moment the entry last changed with THEIR clock. Consumers treat an
 * entry that has not changed within `REMOTE_DRAG_STALE_MS` as gone.
 *
 * Receiver-side stamping keeps clock skew between machines out of the
 * decision; the sender's `at` only exists so an unchanged position still
 * counts as a change when the heartbeat fires.
 */

import type { RemoteCanvasDragNode } from '@/store/yjsStore'

/** An entry whose content has not changed for this long is treated as gone. */
export const REMOTE_DRAG_STALE_MS = 15_000

/** Senders refresh `at` this often while a drag is held still. */
export const REMOTE_DRAG_HEARTBEAT_MS = 5_000

export interface RemoteDragLivenessFields {
  draggingNodes: RemoteCanvasDragNode[] | null
  /** Receiver clock at the last content change; `null`/absent means unknown (treated as live). */
  draggingNodesReceivedAt?: number | null
}

export function dragNodesEqual(a: readonly RemoteCanvasDragNode[], b: readonly RemoteCanvasDragNode[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const nodeA = a[i]
    const nodeB = b[i]
    if (
      nodeA.nodeId !== nodeB.nodeId ||
      nodeA.position.x !== nodeB.position.x ||
      nodeA.position.y !== nodeB.position.y ||
      nodeA.at !== nodeB.at
    ) {
      return false
    }
  }
  return true
}

/**
 * The receiver-side timestamp for a freshly parsed awareness state: carried
 * over from the previous presence when the entry is byte-identical, stamped
 * `now` when it changed (including a heartbeat-only change of `at`).
 */
export function resolveDraggingNodesReceivedAt(
  prev: RemoteDragLivenessFields | undefined,
  next: RemoteCanvasDragNode[] | null,
  now: number,
): number | null {
  if (!next || next.length === 0) return null
  if (
    prev?.draggingNodes &&
    prev.draggingNodesReceivedAt !== null &&
    prev.draggingNodesReceivedAt !== undefined &&
    dragNodesEqual(prev.draggingNodes, next)
  ) {
    return prev.draggingNodesReceivedAt
  }
  return now
}

/** The user's dragged nodes if the entry is still live, else null. */
export function getLiveRemoteDragNodes(
  user: RemoteDragLivenessFields,
  now = Date.now(),
): RemoteCanvasDragNode[] | null {
  const nodes = user.draggingNodes
  if (!nodes || nodes.length === 0) return null
  const receivedAt = user.draggingNodesReceivedAt
  if (receivedAt === null || receivedAt === undefined) return nodes
  return now - receivedAt > REMOTE_DRAG_STALE_MS ? null : nodes
}
